// src/Pages/Estabilishment/Banners/index.tsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { useT } from "../../../i18n";
import { getEstablishmentBanners, deleteEstablishmentBanner, repostEstablishmentBanner } from "../../../Services/Banner";
import { getEstablishmentDashboard } from "../../../Services/EstablishmentDashboard";
import type { IRestDashboard } from "../../../Services/EstablishmentDashboard";
import { IRestGetMySubscription } from "../../../Services/Interfaces";

/** Status da análise da arte pela equipe Pertalks */
export type ArtStatus = "pending" | "approved" | "rejected";

export interface IBanner {
  id: string;
  name: string;
  title: string | null;
  isActive: boolean;
  displayOrder: number;
  line: number | null;
  filename: string | null;
  url: string | null;
  internalUrl: boolean;
  createdAt?: string;

  // ── Aprovação da arte (planos Showcase Pulse/Wave) ──────────────────────
  /** pending = em análise (até 72h) · approved = no ar · rejected = ajustar */
  artStatus?: ArtStatus;
  /** Marco zero do prazo contratado — a veiculação conta a partir daqui */
  artApprovedAt?: string | null;
  /** Motivo da recusa, para o anunciante corrigir e reenviar */
  artRejectionReason?: string | null;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = (p: React.ReactNode, s = 16) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);
const IconEdit    = () => ic(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>);
const IconTrash   = () => ic(<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></>);
const IconRefresh = () => ic(<><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>);
const IconSearch  = () => ic(<><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>);
const IconPlus    = () => ic(<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>);
const IconEmpty   = () => (
  <svg width="160" height="160" viewBox="0 0 200 200" fill="none">
    <circle cx="100" cy="100" r="80" fill="#f5f5f5"/>
    <text x="100" y="115" textAnchor="middle" fontSize="70">📋</text>
  </svg>
);

// ─── Confirm Modal ────────────────────────────────────────────────────────────
const ConfirmModal: React.FC<{
  title: string; body: string;
  confirmLabel: string; confirmColor?: string;
  onConfirm: () => void; onClose: () => void;
  loading?: boolean;
}> = ({ title, body, confirmLabel, confirmColor = "#ef4444", onConfirm, onClose, loading }) => {
  const t = useT();

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", maxWidth: 400, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 700, color: "#111" }}>{title}</h3>
        <p style={{ margin: "0 0 24px", fontSize: 14, color: "#666", lineHeight: 1.5 }}>{body}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{ padding: "9px 20px", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 13, color: "#555" }}>{t("Cancelar")}</button>
          <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 20px", background: confirmColor, border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", display: "flex", alignItems: "center", gap: 6, opacity: loading ? 0.7 : 1 }}>
            {loading && <CircularProgress size={14} style={{ color: "#fff" }} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Toggle Switch ────────────────────────────────────────────────────────────
const Toggle: React.FC<{ checked: boolean }> = ({ checked }) => (
  <div style={{ width: 38, height: 22, borderRadius: 999, background: checked ? "#00b6bc" : "#d1d5db", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
    <div style={{ position: "absolute", top: 3, left: checked ? 18 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
  </div>
);

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination: React.FC<{ page: number; total: number; limit: number; onChange: (p: number) => void }> = ({ page, total, limit, onChange }) => {
  const t = useT();

  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  const pages: (number | "...")[] = [];
  pages.push(1);
  if (page > 3) pages.push("...");
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) pages.push(p);
  if (page < totalPages - 2) pages.push("...");
  if (totalPages > 1) pages.push(totalPages);

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: "6px 12px", border: "1.5px solid", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: active ? 700 : 400,
    borderColor: active ? "#00b6bc" : "#e0e0e0", background: active ? "#00b6bc" : "#fff", color: active ? "#fff" : "#555",
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", marginTop: 24 }}>
      <button disabled={page === 1} onClick={() => onChange(page - 1)} style={{ ...btnStyle(false), opacity: page === 1 ? 0.4 : 1 }}>‹ {t("Anterior")}</button>
      {pages.map((p, i) =>
        p === "..." ? <span key={i} style={{ padding: "0 4px", color: "#aaa" }}>...</span>
          : <button key={i} onClick={() => onChange(p as number)} style={btnStyle(p === page)}>{p}</button>
      )}
      <button disabled={page === totalPages} onClick={() => onChange(page + 1)} style={{ ...btnStyle(false), opacity: page === totalPages ? 0.4 : 1 }}>{t("Próxima")} ›</button>
    </div>
  );
};

// ─── Status da arte ──────────────────────────────────────────────────────────

/**
 * Selo do status da análise da arte.
 *
 * A arte só vai ao ar depois de aprovada pela equipe Pertalks, e o prazo
 * contratado começa a contar a partir da aprovação — não do envio.
 */
const ArtBadge: React.FC<{ status?: string }> = ({ status }) => {
  const t = useT();

  // Os rótulos ficam em português: eles são a própria chave de tradução (ver src/i18n).
  const cfg: Record<string, { label: string; color: string; bg: string }> = {
    approved: { label: "No ar",      color: "#1a7a4a", bg: "#e6f7ef" },
    pending:  { label: "Em análise", color: "#b45309", bg: "#fff7e6" },
    rejected: { label: "Ajustar",    color: "#b91c1c", bg: "#fee2e2" },
  };

  const c = cfg[status ?? "pending"] ?? cfg.pending;

  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 999,
      fontSize: 11, fontWeight: 700, color: c.color, background: c.bg,
      whiteSpace: "nowrap",
    }}>
      {t(c.label)}
    </span>
  );
};

// ─── Plan resolution (Insight / Connection / Showcase[Pulse|Wave]) ──
// Pulse e Wave são o desmembramento do antigo Showcase — por ora têm as
// mesmas permissões, então ambos mapeiam para o tipo interno "showcase".
const resolvePlanType = (name?: string): string => {
  if (!name) return "insight";
  const lower = name.toLowerCase();
  if (lower.includes("showcase") || lower.includes("pulse") || lower.includes("wave")) return "showcase";
  if (lower.includes("connection") || lower.includes("meet"))                          return "connection";
  return "insight";
};

// Banners disponíveis apenas para os planos Connection e Showcase
const planAllowsBanners = (planName?: string) => {
  const type = resolvePlanType(planName);
  return type === "connection" || type === "showcase";
};

// ─── Plan Locked screen — padrão idêntico ao de Notificações Push ────────────
const PlanLocked: React.FC<{ onUpgrade: () => void }> = ({ onUpgrade }) => {
  const t = useT();

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "40px 32px", textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>📢</div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 10px" }}>
        {t("Disponível nos Planos CONNECTION e SHOWCASE")}
      </h3>
      <p style={{ fontSize: 14, color: "#888", margin: "0 0 24px", lineHeight: 1.6 }}>
        {t("A gestão de banners promocionais é uma funcionalidade exclusiva dos planos")}{" "}
        <strong>CONNECTION</strong> {t("e")} <strong>SHOWCASE</strong>.
      </p>
      <button
        onClick={onUpgrade}
        style={{ padding: "11px 28px", background: "#00b6bc", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
      >
        {t("Ver planos disponíveis")}
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export const Banners = () => {
  const navigate = useNavigate();
  const t = useT();
  const [banners,  setBanners]  = useState<IBanner[]>([]);
  // nome do estabelecimento em foco — vem do backend, que respeita o
  // seletor do Dashboard
  const [estabName, setEstabName] = useState("");
  // Plano da unidade em foco — vem na própria listagem. Connection não tem
  // prazo de veiculação contratado, então o aviso de análise muda de texto.
  const [planType, setPlanType] = useState("");
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const LIMIT = 10;

  // Subscription plan check
  const [planAllowed,   setPlanAllowed]   = useState<boolean | null>(null); // null = carregando
  // Connection com documentos ainda em análise: plano permite, área fechada
  const [pendingApproval, setPendingApproval] = useState(false);
  const [docsStatus,      setDocsStatus]      = useState<string>("");

  useEffect(() => {
    getEstablishmentDashboard()
      .then((res) => {
        const d = (res as IRestDashboard).data.data;
        setPlanAllowed(!!d.allowsBanner);
        setDocsStatus(d.documentsStatus ?? "");
        // nome vem do dashboard: quando bloqueado, a listagem responde 403
        // e não traria o estabelecimento
        setEstabName(d.establishment?.name ?? "");
        // plano correto, mas documentos ainda não aprovados
        setPendingApproval(
          !d.allowsBanner && d.planType === "connection" && !d.documentsApproved,
        );
      })
      .catch(() => setPlanAllowed(false));
  }, []);

  // Modals
  const [deleteTarget,  setDeleteTarget]  = useState<IBanner | null>(null);
  const [repostTarget,  setRepostTarget]  = useState<IBanner | null>(null);
  const [modalLoading,  setModalLoading]  = useState(false);

  const load = useCallback((p = 1, q = "") => {
    setLoading(true);
    getEstablishmentBanners(q, (p - 1) * LIMIT, LIMIT)
      .then((res: any) => {
        setBanners(res.data.data ?? []);
        setTotal(res.data.pagination?.total ?? 0);
        setEstabName(res.data.estabilishment?.name ?? "");
        setPlanType(res.data.planType ?? "");
      })
      .catch(() => toast.error(t("Erro ao carregar banners.")))
      .finally(() => setLoading(false));
  // `t` fica fora das deps de propósito: incluí-lo refaria a consulta a
  // cada troca de idioma, perdendo página, filtros e rolagem.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(1, ""); }, [load]);

  const handleSearch = (q: string) => {
    setSearch(q); setPage(1); load(1, q);
  };

  const handlePage = (p: number) => {
    setPage(p); load(p, search);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setModalLoading(true);
    deleteEstablishmentBanner(deleteTarget.id)
      .then(() => { toast.success(t("Banner excluído com sucesso.")); setDeleteTarget(null); load(page, search); })
      .catch(() => toast.error(t("Não foi possível excluir o banner. Por favor, tente novamente.")))
      .finally(() => setModalLoading(false));
  };

  const handleRepost = () => {
    if (!repostTarget) return;
    setModalLoading(true);
    repostEstablishmentBanner(repostTarget.id)
      .then(() => { setRepostTarget(null); load(page, search); })
      .catch(() => {})
      .finally(() => setModalLoading(false));
  };

  const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "#888", borderBottom: "1.5px solid #f0f0f0", whiteSpace: "nowrap" };
  const tdStyle: React.CSSProperties = { padding: "12px 14px", fontSize: 13, color: "#333", borderBottom: "1px solid #f7f7f7", verticalAlign: "middle" };

  // ── Plan gate ──────────────────────────────────────────────────────────────
  if (planAllowed === null) return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
      <CircularProgress style={{ color: "#00b6bc" }} />
    </div>
  );

  // Documentos em análise — plano correto, área ainda fechada
  if (planAllowed === false && pendingApproval) return (
    <div style={{ maxWidth: 560, padding: 16 }}>
      <h2 style={{ margin: "0 0 20px", fontSize: 22, fontWeight: 700, color: "#111" }}>{t("Banners")}</h2>
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 10px" }}>
          {estabName
            ? t("Estamos analisando os documentos de {nome}", { nome: estabName })
            : t("Estamos analisando os documentos")}
        </h3>
        <p style={{ fontSize: 14, color: "#888", margin: "0 0 8px", lineHeight: 1.6 }}>
          {t("A área de banners desta unidade é liberada assim que a análise for concluída — o prazo é de até")}{" "}
          <strong>{t("72 horas")}</strong>.
        </p>
        <p style={{ fontSize: 13, color: "#888", margin: "0 0 8px", lineHeight: 1.6 }}>
          {t("Se você tem outra unidade já aprovada, troque no seletor do Dashboard para usar os banners dela normalmente.")}
        </p>
        <p style={{ fontSize: 13, color: "#aaa", margin: 0, lineHeight: 1.6 }}>
          {t("Enquanto isso, o restante do painel continua disponível.")}
          {docsStatus === "rejected" &&
            " " + t("Encontramos uma divergência nos documentos — verifique seu e-mail.")}
        </p>
      </div>
    </div>
  );

  if (planAllowed === false) return (
    <div style={{ maxWidth: 560, padding: 16 }}>
      <h2 style={{ margin: "0 0 20px", fontSize: 22, fontWeight: 700, color: "#111" }}>{t("Banners")}</h2>
      <PlanLocked onUpgrade={() => navigate("/establishment/subscription")} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 700, color: "#111" }}>{t("Banners")}</h2>

      {/* Deixa claro a qual estabelecimento os banners pertencem — a escolha
          vem do seletor do Dashboard e não é óbvia nesta tela. */}
      {estabName && (
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#888" }}>
          {t("Banners de")}{" "}
          <strong style={{ color: "#555" }}>{estabName}</strong>.{" "}
          {t("Para trocar, use o seletor no Dashboard.")}
        </p>
      )}

      {/* Search + Create */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 380 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#aaa" }}><IconSearch /></span>
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder={t("Pesquisar...")}
            style={{ width: "100%", padding: "9px 12px 9px 38px", border: "1.5px solid #e8e8e8", borderRadius: 10, fontSize: 13, color: "#333", outline: "none", boxSizing: "border-box" }}
          />
        </div>
        <button
          onClick={() => navigate("/establishment/banners/create")}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#00b6bc", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0 }}
        >
          <IconPlus /> {t("Cadastrar")}
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress style={{ color: "#00b6bc" }} />
        </div>
      ) : banners.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <IconEmpty />
          <p style={{ fontSize: 14, color: "#aaa", marginTop: 16 }}>{t("Ainda não há banners para serem exibidos.")}</p>
        </div>
      ) : (
        <>
          {/* Como funciona a análise da arte */}
          {banners.some(b => b.artStatus === "pending") && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              background: "#fff7e6", border: "1px solid #fde68a", borderRadius: 12,
              padding: "12px 14px", marginBottom: 14,
            }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>⏳</span>
              <p style={{ margin: 0, fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
                {t("Você tem arte em análise. Nossa equipe avalia em até")}{" "}
                <strong>{t("72 horas")}</strong>.{" "}
                {planType === "connection"
                  ? t("Assim que aprovada, ela entra na vitrine automaticamente.")
                  : t("Assim que aprovada, ela entra na vitrine automaticamente — e só a partir desse momento começa a contar o prazo de veiculação que você contratou.")}
              </p>
            </div>
          )}

          {banners.some(b => b.artStatus === "rejected") && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 12,
              padding: "12px 14px", marginBottom: 14,
            }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: 13, color: "#b91c1c", lineHeight: 1.6 }}>
                {t("Uma das suas artes precisa de ajuste. Veja o motivo na coluna")}{" "}
                <strong>{t("Arte")}</strong>
                {t(", corrija e reenvie — não há limite de tentativas e o prazo contratado não é consumido enquanto isso.")}
              </p>
            </div>
          )}

          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#fafafa" }}>
                <tr>
                  <th style={thStyle}>{t("Nome")}</th>
                  <th style={thStyle}>{t("Ordenação")}</th>
                  <th style={thStyle}>{t("Data")}</th>
                  <th style={thStyle}>{t("Arte")}</th>
                  <th style={thStyle}>{t("Status")}</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>{t("Ações")}</th>
                </tr>
              </thead>
              <tbody>
                {banners.map(b => (
                  <tr key={b.id} style={{ transition: "background 0.1s" }} onMouseEnter={e => (e.currentTarget.style.background = "#fafafa")} onMouseLeave={e => (e.currentTarget.style.background = "")}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: "#111", marginBottom: 2 }}>{b.name}</div>
                      {b.title && <div style={{ fontSize: 11, color: "#aaa" }}>{b.title}</div>}
                    </td>
                    <td style={tdStyle}>{b.displayOrder ?? "—"}</td>
                    <td style={tdStyle}>{b.createdAt ? new Date(b.createdAt).toLocaleDateString("pt-BR") : "—"}</td>
                    <td style={tdStyle}>
                      <ArtBadge status={b.artStatus} />
                      {b.artStatus === "rejected" && b.artRejectionReason && (
                        <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 4, maxWidth: 220, lineHeight: 1.4 }}>
                          {b.artRejectionReason}
                        </div>
                      )}
                      {b.artStatus === "approved" && b.artApprovedAt && (
                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                          {t("desde {data}", {
                            data: new Date(b.artApprovedAt.replace(" ", "T")).toLocaleDateString("pt-BR"),
                          })}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}><Toggle checked={b.isActive} /></td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button title={t("Editar")} onClick={() => navigate(`/establishment/banners/${b.id}/edit`)} style={{ width: 32, height: 32, border: "none", borderRadius: 8, background: "#e6f9fa", color: "#00b6bc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><IconEdit /></button>
                        <button title={t("Repostar")} onClick={() => setRepostTarget(b)} style={{ width: 32, height: 32, border: "none", borderRadius: 8, background: "#e6f9fa", color: "#00b6bc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><IconRefresh /></button>
                        <button title={t("Excluir")} onClick={() => setDeleteTarget(b)} style={{ width: 32, height: 32, border: "none", borderRadius: 8, background: "#fde8e8", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><IconTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} total={total} limit={LIMIT} onChange={handlePage} />
        </>
      )}

      {/* Delete Modal */}
      {deleteTarget && (
        <ConfirmModal
          title={t("Excluir banner")}
          body={t("Tem certeza que deseja excluir este banner?")}
          confirmLabel={t("Excluir")}
          confirmColor="#ef4444"
          loading={modalLoading}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {/* Repost Modal */}
      {repostTarget && (
        <ConfirmModal
          title={t("Repostar banner")}
          body={t("Ao repostar este banner, as configurações e imagem serão reutilizadas. Tem certeza que deseja repostar este banner?")}
          confirmLabel={t("Repostar")}
          confirmColor="#00b6bc"
          loading={modalLoading}
          onConfirm={handleRepost}
          onClose={() => setRepostTarget(null)}
        />
      )}
    </div>
  );
};